/**
 * One conversation as a surface draws it: the thread merged with this browser's sends, its paging,
 * the read mark, typing in both directions, and the sends themselves. A thin binding of the chat
 * session (`model/chatSession`) to the thread's query — every rule lives there.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useSessionUser } from '~/entities/session';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { threadQueryOptions } from '../api/chatQueries';
import { mergeThread, newestServerId, type ChatMessage } from '../model/chatMessage';
import type { ChatNotice, SendIntent } from '../model/chatSession';
import { threadMessages } from '../model/threadCache';

import { useChatSession, useSessionVersion } from './chatSessionContext';

export interface Conversation {
  readonly messages: readonly ChatMessage[];
  readonly pending: boolean;
  readonly failed: boolean;
  readonly hasOlder: boolean;
  readonly loadingOlder: boolean;
  readonly loadOlder: () => void;
  readonly refetch: () => void;
  readonly typing: boolean;
  /** Returns the send's `clientId`. Never throws; a refusal is a notice, a failure a `failed` bubble. */
  readonly send: (intent: SendIntent) => string;
  readonly retry: (clientId: string) => void;
  readonly remove: (clientId: string) => void;
  /** The trainer is typing; throttled to one signal a second. */
  readonly typed: () => void;
}

function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const onChange = () => {
      setVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', onChange);
    return () => {
      document.removeEventListener('visibilitychange', onChange);
    };
  }, []);
  return visible;
}

export function useConversation(
  conversationId: number,
  onNotice: (notice: ChatNotice) => void,
): Conversation {
  const session = useChatSession();
  const version = useSessionVersion(session);
  const me = useSessionUser();
  const lang = useContentLanguage();
  const visible = usePageVisible();
  const query = useInfiniteQuery(threadQueryOptions(conversationId, lang));

  useEffect(() => {
    session.join(conversationId);
  }, [session, conversationId]);

  useEffect(() => session.onNotice(onNotice), [session, onNotice]);

  const messages = useMemo(
    () =>
      mergeThread(
        threadMessages(query.data),
        session.pendingFor(conversationId),
        session.previews(),
        me?.id ?? null,
      ),
    // `version` is the session's change counter: the pending sends and previews are read through it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, session, conversationId, me?.id, version],
  );

  const newest = newestServerId(messages);
  useEffect(() => {
    session.seen(conversationId, newest, visible);
  }, [session, conversationId, newest, visible]);

  return {
    messages,
    pending: query.isPending,
    failed: query.isError && query.data === undefined,
    hasOlder: query.hasNextPage,
    loadingOlder: query.isFetchingNextPage,
    loadOlder: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
    refetch: () => {
      void query.refetch();
    },
    typing: session.isTyping(conversationId),
    send: (intent) => session.send(conversationId, intent),
    retry: session.retry,
    remove: session.remove,
    typed: () => {
      session.typed(conversationId);
    },
  };
}
